<script>
/* eslint no-console: 0 */
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';

export default {
  components: {
    NextButton,
    Modal,
    WootMessageEditor,
  },
  props: {
    id: { type: Number, default: null },
    edcontent: { type: String, default: '' },
    edshortCode: { type: String, default: '' },
    edteamId: { type: Number, default: null },
    edattachments: { type: Array, default: () => [] },
    onClose: { type: Function, default: () => {} },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      editCanned: {
        showAlert: false,
        showLoading: false,
      },
      shortCode: this.edshortCode,
      content: this.edcontent,
      teamId: this.edteamId ?? '',
      existingFiles: [...this.edattachments],
      removedFileIds: [],
      pendingFiles: [],
      isUploading: false,
      show: true,
    };
  },
  validations: {
    shortCode: {
      required,
      minLength: minLength(2),
    },
    content: {
      required,
    },
  },
  computed: {
    pageTitle() {
      return `${this.$t('CANNED_MGMT.EDIT.TITLE')} - ${this.edshortCode}`;
    },
    teams() {
      return this.$store.getters['teams/getTeams'];
    },
  },
  methods: {
    setPageName({ name }) {
      this.v$.content.$touch();
      this.content = name;
    },
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    async onFileInputChange(event) {
      const files = Array.from(event.target.files || []);
      this.isUploading = true;
      await Promise.all(
        files.map(async file => {
          try {
            const { blobId, fileUrl } = await uploadFile(file);
            this.pendingFiles.push({
              blobId,
              fileUrl,
              filename: file.name,
              fileType: file.type,
            });
          } catch (error) {
            useAlert(
              this.$t('CANNED_MGMT.ATTACHMENTS.UPLOAD_ERROR', {
                fileName: file.name,
              })
            );
          }
        })
      );
      this.isUploading = false;
      event.target.value = '';
    },
    removePendingFile(index) {
      this.pendingFiles.splice(index, 1);
    },
    removeExistingFile(index) {
      const [removed] = this.existingFiles.splice(index, 1);
      this.removedFileIds.push(removed.id);
    },
    editCannedResponse() {
      // Show loading on button
      this.editCanned.showLoading = true;
      // Make API Calls
      this.$store
        .dispatch('updateCannedResponse', {
          id: this.id,
          short_code: this.shortCode,
          content: this.content,
          team_id: this.teamId === '' ? null : this.teamId,
          attached_file_blob_ids: this.pendingFiles.map(file => file.blobId),
          file_ids_to_remove: this.removedFileIds,
        })
        .then(() => {
          // Reset Form, Show success message
          this.editCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.EDIT.API.SUCCESS_MESSAGE'));
          this.resetForm();
          setTimeout(() => {
            this.onClose();
          }, 10);
        })
        .catch(error => {
          this.editCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.EDIT.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="pageTitle" />
      <form class="flex flex-col w-full" @submit.prevent="editCannedResponse()">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.PLACEHOLDER')"
              @input="v$.shortCode.$touch"
            />
          </label>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.content.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.CONTENT.LABEL') }}
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              :class="{ editor_warning: v$.content.$error }"
              channel-type="Context::Default"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.CONTENT.PLACEHOLDER')"
              @blur="v$.content.$touch"
            />
          </div>
        </div>
        <div class="w-full">
          <label>
            {{ $t('CANNED_MGMT.EDIT.FORM.TEAM.LABEL') }}
            <select v-model="teamId">
              <option value="">
                {{ $t('CANNED_MGMT.EDIT.FORM.TEAM.GLOBAL') }}
              </option>
              <option v-for="team in teams" :key="team.id" :value="team.id">
                {{ team.name }}
              </option>
            </select>
          </label>
        </div>
        <div class="w-full">
          <label>{{ $t('CANNED_MGMT.ATTACHMENTS.LABEL') }}</label>
          <div
            v-if="existingFiles.length || pendingFiles.length"
            class="flex flex-wrap items-center gap-2 mt-1 mb-2"
          >
            <div
              v-for="(file, index) in existingFiles"
              :key="`existing-${file.id}`"
              class="flex items-center gap-1 py-1 px-2 rounded-md bg-n-alpha-black2"
            >
              <img
                v-if="file.file_type.startsWith('image')"
                :src="file.file_url"
                class="object-cover w-10 h-10 rounded"
              />
              <span class="text-xs truncate max-w-[10rem]">
                {{ file.filename }}
              </span>
              <NextButton
                xs
                slate
                faded
                icon="i-lucide-x"
                @click.prevent="removeExistingFile(index)"
              />
            </div>
            <div
              v-for="(file, index) in pendingFiles"
              :key="file.blobId"
              class="flex items-center gap-1 py-1 px-2 rounded-md bg-n-alpha-black2"
            >
              <img
                v-if="file.fileType.startsWith('image')"
                :src="file.fileUrl"
                class="object-cover w-10 h-10 rounded"
              />
              <span class="text-xs truncate max-w-[10rem]">
                {{ file.filename }}
              </span>
              <NextButton
                xs
                slate
                faded
                icon="i-lucide-x"
                @click.prevent="removePendingFile(index)"
              />
            </div>
          </div>
          <input
            type="file"
            accept="image/*,video/*"
            multiple
            :disabled="isUploading"
            @change="onFileInputChange"
          />
          <span v-if="isUploading" class="text-xs text-n-slate-11">
            {{ $t('CANNED_MGMT.ATTACHMENTS.UPLOADING') }}
          </span>
        </div>
        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CANNED_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="$t('CANNED_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="
              v$.content.$invalid ||
              v$.shortCode.$invalid ||
              editCanned.showLoading ||
              isUploading
            "
            :is-loading="editCanned.showLoading"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
::v-deep {
  .ProseMirror-menubar {
    @apply hidden;
  }

  .ProseMirror-woot-style {
    @apply min-h-[12.5rem];

    p {
      @apply text-base;
    }
  }
}
</style>
